"use client"

import { useState, useEffect, useMemo, useRef } from "react"
import Modal from "../modules/Modal/Moadal"
import InfoCard from "../modules/InfoCard/InfoCard"
import Button from "../modules/Button/Button"
import StatusSelector from "../detail/StatusSelector"
import OrderPdfExporter from "../sections/OrderPdfExporter"
import styles from "../../../styles/inventory/orders-page.module.css"

import { updateClientBulkOrderStatus, updateTenoviBulkOrderStatus } from "../../../services/inventory/detail"
import { parseDateSafe } from "../../../utils/dateFormatters"

const OrderDetailModal = ({ isOpen, onClose, order, onStatusChange }) => {
  const [editableOrder, setEditableOrder] = useState(order)
  const [statusColorMap, setStatusColorMap] = useState({})
  const statusSelectorRef = useRef(null)

  useEffect(() => {
    setEditableOrder(order)
  }, [order])

  useEffect(() => {
    const colorMap = {
      pending: { color: "#fef3c7", text: "#d97706", dotColor: "#d97706" },
      processing: { color: "#dbeafe", text: "#2563eb", dotColor: "#2563eb" },
      shipped: { color: "#dcfce7", text: "#16a34a", dotColor: "#16a34a" },
      delivered: { color: "#d1fae5", text: "#065f46", dotColor: "#065f46" },
      cancelled: { color: "#fee2e2", text: "#dc2626", dotColor: "#dc2626" },
    }
    setStatusColorMap(colorMap)
  }, [])

  const statusColorMapMemo = useMemo(() => statusColorMap, [statusColorMap])

  const handleStatusChange = async (newStatus) => {
    try {
      setEditableOrder((prev) => ({ ...prev, status: newStatus }))

      if (editableOrder?.originalOrder?.client_bulkorderid) {
        await updateClientBulkOrderStatus(editableOrder.id, newStatus)
      } else if (editableOrder?.originalOrder?.id) {
        await updateTenoviBulkOrderStatus(editableOrder.id, newStatus)
      }

      if (onStatusChange) {
        onStatusChange(newStatus, order)
      }

      console.log(`Order ${editableOrder?.orderNumber} updated to: ${newStatus}`)
    } catch (error) {
      console.error("Failed to update status:", error)
      alert("Status update failed. Please try again.")
    }
  }

  const handleCancelOrder = async () => {
    try {
      setEditableOrder((prev) => ({ ...prev, status: "cancelled" }))

      if (statusSelectorRef.current?.closeDropdown) {
        statusSelectorRef.current.closeDropdown()
      }

      if (editableOrder?.originalOrder?.client_bulkorderid) {
        await updateClientBulkOrderStatus(editableOrder.id, "cancelled")
      } else if (editableOrder?.originalOrder?.id) {
        await updateTenoviBulkOrderStatus(editableOrder.id, "cancelled")
      }

      if (onStatusChange) {
        onStatusChange("cancelled", order)
      }

      console.log(`Order ${editableOrder?.orderNumber} has been cancelled.`)
      onClose()
    } catch (error) {
      console.error("Cancel API failed:", error)
      alert("Failed to cancel order.")
    }
  }

  const handlePrintLabel = () => {
    try {
      if (editableOrder) {
        OrderPdfExporter.generate(editableOrder, statusColorMapMemo, {
          type: "order",
          fileName: `order-${editableOrder.orderNumber || editableOrder.id}.pdf`,
        })
      }
    } catch (err) {
      console.error("Error generating PDF:", err)
      alert("Failed to generate PDF")
    }
  }

  const modalFooter = (
    <div className={styles.order_detail_modal_footer_buttons}>
      <Button variant="secondary" size="small" onClick={handlePrintLabel} disabled={false} title="Download PDF">
        Print Label
      </Button>
      <Button variant="secondary" size="small" onClick={onClose}>
        Close
      </Button>
      <Button
        variant="danger"
        onClick={handleCancelOrder}
        disabled={editableOrder?.status === "cancelled" || editableOrder?.status === "delivered"}
        size="small"
      >
        Cancel Order
      </Button>
    </div>
  )

  if (!order) return null

  const devicesList =
    editableOrder?.originalOrder?.items?.map((item) => `${item.device_type_name} - (${item.quantity})`) || []

  const devicesDisplay =
    devicesList.length > 0 ? (
      <ul style={{ margin: 0 }}>
        {devicesList.map((d, idx) => (
          <li key={idx}>{d}</li>
        ))}
      </ul>
    ) : (
      "N/A"
    )

  const orderInfoData = [
    { label: "Order ID", value: editableOrder?.orderNumber },
    { label: "Ordered By", value: editableOrder?.orderedBy || "N/A" },
    {
      label: "Status",
      value: (
        <StatusSelector
          ref={statusSelectorRef}
          currentStatus={editableOrder?.status}
          onStatusChange={handleStatusChange}
          statusColorMap={statusColorMapMemo}
          disabled={editableOrder?.status === "delivered" || editableOrder?.status === "cancelled"}
        />
      ),
    },
    { label: "Tenant Name", value: editableOrder?.tenantName || "N/A" },
    { label: "Location", value: editableOrder?.location || "N/A" },
    { label: "Devices", value: devicesDisplay },
    { label: "Total Devices", value: editableOrder?.devices || 0 },
    { label: "Order Date", value: editableOrder?.orderDate },
    // { label: "Delivered Date", value: editableOrder?.delivered_at },
    {
      label: "Canceled At",
      value: editableOrder?.canceledAt ? parseDateSafe(editableOrder.canceledAt)?.toLocaleDateString() || "N/A" : "N/A",
    },
    {
      label: "Delivered At",
      value: editableOrder?.deliveredAt ? parseDateSafe(editableOrder.deliveredAt)?.toLocaleDateString() || "N/A" : "N/A",
    },
    {
      label: "Delivered By",
      value: editableOrder?.deliveredBy ? parseDateSafe(editableOrder.deliveredBy)?.toLocaleDateString() || "N/A" : "N/A",
    },
    { label: "Cancelled By", value: editableOrder?.cancelledBy || "N/A" },
  ]

  return (
    <Modal
      title={`Order Details - ${editableOrder?.orderNumber}`}
      isOpen={isOpen}
      onClose={onClose}
      width="800px"
      height="auto"
      footer={modalFooter}
    >
      <div className={styles.order_detail_modal_content}>
        <InfoCard title="Order Information" data={orderInfoData} />
      </div>
    </Modal>
  )
}

export default OrderDetailModal
